import Button from '@blocks/Button'
import useDebounce from '@hooks/useDebounce'
import useStatic from '@hooks/useStatic'
import { ReactComponent as H3Icon } from '@icons/h3.svg'
import { ReactComponent as H4Icon } from '@icons/h4.svg'
import { ReactComponent as OlIcon } from '@icons/ol.svg'
import { ReactComponent as UlIcon } from '@icons/ul.svg'
import React, { useCallback, useRef, useState } from 'react'
import { BaseEditor, createEditor, Descendant } from 'slate'
import { Editable, ReactEditor, Slate, withReact } from 'slate-react'
import BlockButton from './components/BlockButton'
import EditableWrapper from './components/EditableWrapper'
import Element, { ElementInterface, TextInterface } from './components/Element'
import Header from './components/Header'
import RightBar from './components/RightBar'
import Toolbar from './components/Toolbar'
import Wrapper from './components/Wrapper'
import useStatus from './hooks/useStatus'

export default function RichEditor(props: Props) {
    const [editor] = useState(() => withReact(createEditor()))
    const [value, setValue] = useState<Descendant[]>(props.value || [])
    const [status, saving] = useStatus()

    const handleClick = useCallback(async () => {
        if (!props.onSave)
            return

        await saving(() => props.onSave!(value))
    }, [value])

    const handleChange = useStatic(async (value: Descendant[]) => {
        setValue(value)

        if (!props.onChange)
            return

        const isActiveChange = editor.operations.some(
            op => 'set_selection' !== op.type
        )
        
        if (!isActiveChange)
            return

        await saving(() => props.onChange!(value))
    })

    return (
        <Wrapper readOnly={props.readOnly}>
            <Slate
                editor={editor}
                value={value}
                onChange={handleChange}>
                <Header readOnly={props.readOnly}>
                    <Toolbar>
                        <BlockButton format="h3" icon={<H3Icon />} />
                        <BlockButton format="h4" icon={<H4Icon />} />
                        <BlockButton format="ul" icon={<UlIcon />} />
                    </Toolbar>
                    <RightBar>
                        <small>{status}</small>
                        <Button width="auto" type="tertiary" onClick={handleClick}>Save</Button>
                    </RightBar>
                </Header>
                <EditableWrapper readOnly={props.readOnly}>
                    <Editable 
                        readOnly={props.readOnly}
                        renderElement={Element} />
                </EditableWrapper>
            </Slate>
        </Wrapper>
    )
}

interface Props {
    value?: Descendant[]
    onSave?: (value: Descendant[]) => Promise<void>
    onChange?: (value: Descendant[]) => Promise<void>
    readOnly?: boolean
}

declare module 'slate' {
    interface CustomTypes {
        Editor: BaseEditor & ReactEditor
        Element: ElementInterface
        Text: TextInterface
    }
}